import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, AlertTriangle, ChevronRight, Scale, FileCheck, Building, Globe } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../context/LanguageContext';
import { SEO } from '../components/SEO';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
};

export default function Compliance() {
  const { language } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Layout>
      <SEO
        title={language === 'es' ? 'Cumplimiento Regulatorio' : 'Regulatory Compliance'}
        description={language === 'es' 
          ? 'Compromiso de PeCashGlobal con el cumplimiento legal y regulatorio.'
          : 'PeCashGlobal\'s commitment to legal and regulatory compliance.'}
        canonical="https://pecashglobal.com/compliance"
        lang={language}
      />
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 py-16">
          <div className="max-w-4xl mx-auto px-6">
            <motion.div {...fadeInUp} className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 text-amber-400 rounded-full text-sm mb-6">
                <AlertTriangle className="w-4 h-4" />
                {language === 'es' ? 'Borrador - Revisión Legal Pendiente' : 'Draft - Legal Review Pending'}
              </div>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500/20 rounded-full mb-6">
                <Scale className="w-8 h-8 text-blue-400" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {language === 'es' ? 'Cumplimiento Regulatorio' : 'Regulatory Compliance'}
              </h1>
              <p className="text-slate-400 max-w-2xl mx-auto">
                {language === 'es' 
                  ? 'Nuestro compromiso con el cumplimiento legal y regulatorio'
                  : 'Our commitment to legal and regulatory compliance'}
              </p>
            </motion.div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-6 py-12">
          {/* Critical Legal Notice */}
          <motion.div 
            {...fadeInUp}
            className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8"
          >
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-900 mb-2">
                  {language === 'es' ? 'Aviso Legal Crítico' : 'Critical Legal Notice'}
                </h3>
                <p className="text-red-800 text-sm mb-4">
                  {language === 'es' 
                    ? 'La clasificación regulatoria de PeCashGlobal está PENDIENTE DE REVISIÓN LEGAL. No declaramos ni implicamos:'
                    : 'PeCashGlobal\'s regulatory classification is PENDING LEGAL REVIEW. We do not state or imply:'}
                </p>
                <ul className="text-red-800 text-sm space-y-1 list-disc list-inside">
                  <li>{language === 'es' ? 'Exención de regulación de transmisión de dinero' : 'Exemption from money transmitter regulation'}</li>
                  <li>{language === 'es' ? 'Que no somos un transmisor de dinero' : 'That we are not a money transmitter'}</li>
                  <li>{language === 'es' ? 'Aprobación regulatoria que no tenemos' : 'Regulatory approval we do not have'}</li>
                  <li>{language === 'es' ? 'Licencias que no hemos obtenido' : 'Licenses we have not obtained'}</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Operating Structure */}
          <motion.section
            {...fadeInUp}
            className="bg-white rounded-xl border border-slate-200 p-6 mb-8"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Building className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900 mb-2">
                  {language === 'es' ? 'Estructura Operativa' : 'Operating Structure'}
                </h2>
                <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">
                  LEGAL REVIEW REQUIRED
                </span>
              </div>
            </div>
            
            <div className="space-y-4 text-slate-600">
              <p>
                {language === 'es'
                  ? 'PeCashGlobal trabaja con socios de pago regulados y licenciados y no retiene fondos de clientes. Los servicios de billetera y pago son proporcionados por instituciones financieras licenciadas. PeCashGlobal opera como plataforma tecnológica y administrador de programas.'
                  : 'PeCashGlobal works with regulated and licensed payment partners and does not hold customer funds. Wallet and payment services are provided by licensed financial institutions. PeCashGlobal operates as a technology platform and program manager.'}
              </p>
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm font-medium text-slate-700 mb-2">
                  {language === 'es' ? '[PENDIENTE DE COMPLETAR]' : '[PENDING COMPLETION]'}
                </p>
                <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
                  <li>{language === 'es' ? 'Nombre de socio de pago' : 'Payment partner name'}</li>
                  <li>{language === 'es' ? 'Información de licencia del socio' : 'Partner license information'}</li>
                  <li>{language === 'es' ? 'Números de registro aplicables' : 'Applicable registration numbers'}</li>
                </ul>
              </div>
            </div>
          </motion.section>

          {/* AML/KYC */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl border border-slate-200 p-6 mb-8"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <FileCheck className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900 mb-2">
                  {language === 'es' ? 'Anti-Lavado de Dinero (AML) / Conozca a Su Cliente (KYC)' : 'Anti-Money Laundering (AML) / Know Your Customer (KYC)'}
                </h2>
                <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">
                  LEGAL REVIEW REQUIRED
                </span>
              </div>
            </div>
            
            <div className="space-y-4 text-slate-600">
              <p>
                {language === 'es'
                  ? 'PeCashGlobal se compromete a prevenir el lavado de dinero y el financiamiento del terrorismo. Implementamos procedimientos para:'
                  : 'PeCashGlobal is committed to preventing money laundering and terrorism financing. We implement procedures to:'}
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2" />
                  <span>
                    {language === 'es' 
                      ? 'Verificar la identidad de los clientes'
                      : 'Verify customer identity'}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2" />
                  <span>
                    {language === 'es' 
                      ? 'Monitorear transacciones en busca de actividad sospechosa'
                      : 'Monitor transactions for suspicious activity'}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2" />
                  <span>
                    {language === 'es' 
                      ? 'Reportar actividad sospechosa a las autoridades correspondientes'
                      : 'Report suspicious activity to appropriate authorities'}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2" />
                  <span>
                    {language === 'es' 
                      ? 'Cumplir con las regulaciones de sanciones de la OFAC'
                      : 'Comply with OFAC sanctions regulations'}
                  </span>
                </li>
              </ul>
            </div>
          </motion.section>

          {/* Data Protection */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-white rounded-xl border border-slate-200 p-6 mb-8"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900 mb-2">
                  {language === 'es' ? 'Protección de Datos' : 'Data Protection'}
                </h2>
                <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">
                  LEGAL REVIEW REQUIRED
                </span>
              </div>
            </div>
            
            <div className="space-y-4 text-slate-600">
              <p>
                {language === 'es'
                  ? 'Cumplimos con las leyes de protección de datos aplicables, incluyendo:'
                  : 'We comply with applicable data protection laws, including:'}
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h3 className="font-medium text-slate-900 mb-2">CCPA</h3>
                  <p className="text-sm">
                    {language === 'es'
                      ? 'Ley de Privacidad del Consumidor de California'
                      : 'California Consumer Privacy Act'}
                  </p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h3 className="font-medium text-slate-900 mb-2">GDPR</h3>
                  <p className="text-sm">
                    {language === 'es'
                      ? 'Reglamento General de Protección de Datos (si aplica)'
                      : 'General Data Protection Regulation (if applicable)'}
                  </p>
                </div>
              </div>
            </div>
          </motion.section>

          {/* International Operations */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl border border-slate-200 p-6 mb-8"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Globe className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900 mb-2">
                  {language === 'es' ? 'Operaciones Internacionales' : 'International Operations'}
                </h2>
                <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">
                  LEGAL REVIEW REQUIRED
                </span>
              </div>
            </div>
            
            <div className="space-y-4 text-slate-600">
              <p>
                {language === 'es'
                  ? 'PeCashGlobal facilita transacciones entre Estados Unidos y la República Dominicana. Cumplimos con:'
                  : 'PeCashGlobal facilitates transactions between the United States and the Dominican Republic. We comply with:'}
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-amber-500 rounded-full mt-2" />
                  <span>
                    {language === 'es' 
                      ? 'Regulaciones de transferencia transfronteriza de EE.UU.'
                      : 'U.S. cross-border transfer regulations'}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-amber-500 rounded-full mt-2" />
                  <span>
                    {language === 'es' 
                      ? 'Regulaciones de la República Dominicana aplicables'
                      : 'Applicable Dominican Republic regulations'}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-amber-500 rounded-full mt-2" />
                  <span>
                    {language === 'es' 
                      ? 'Requisitos de la Oficina de Protección Financiera del Consumidor (CFPB)'
                      : 'Consumer Financial Protection Bureau (CFPB) requirements'}
                  </span>
                </li>
              </ul>
            </div>
          </motion.section>

          {/* Contact */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-blue-50 border border-blue-200 rounded-xl p-6"
          >
            <h2 className="text-xl font-semibold text-blue-900 mb-4">
              {language === 'es' ? 'Preguntas de Cumplimiento' : 'Compliance Questions'}
            </h2>
            <p className="text-blue-800 mb-4">
              {language === 'es' 
                ? 'Para preguntas sobre nuestras prácticas de cumplimiento, contáctenos en:'
                : 'For questions about our compliance practices, contact us at:'}
            </p>
            <p className="text-blue-900 font-medium">support@pecashglobal.com</p>
          </motion.section>

          {/* Navigation */}
          <motion.div 
            {...fadeInUp}
            className="mt-12 flex flex-wrap gap-4 justify-center"
          >
            <Link 
              to="/terms"
              className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-full hover:bg-slate-800 transition-colors"
            >
              {language === 'es' ? 'Términos de Servicio' : 'Terms of Service'}
              <ChevronRight className="w-4 h-4" />
            </Link>
            <Link 
              to="/privacy"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-slate-900 border border-slate-200 rounded-full hover:bg-slate-50 transition-colors"
            >
              {language === 'es' ? 'Política de Privacidad' : 'Privacy Policy'}
              <ChevronRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
}
